import * as THREE from 'three';
import { bronze } from './hud-materials.js';
import { boltGeometry, copiesGeometry, iron } from './hud-parts.js';
import { BUTTON, ButtonKit, OctagonButton } from './octagon-button.js';

/** One button on the bottom bar as the HUD wants it drawn. */
export interface DockButton {
  id: string; icon: string;
  /** Held down and glowing: its panel is open, or the game is paused. */
  pressed?: boolean; disabled?: boolean;
  /** Drawn larger, at the bar's end (Pause). */
  large?: boolean;
  /** Starts a new run of buttons, a little apart from the one before. */
  apart?: boolean;
  tint?: number;
}

const STEP = BUTTON * 2.6, APART = BUTTON * 1.1, LARGE = 1.3;
const ease = (t: number) => 1 - (1 - t) ** 3;

/**
 * The bottom bar: a bronze rail bolted at both ends with riveted octagon buttons along it, the same make as the radial menu's. It is
 * rebuilt only when its run of buttons changes; pressed, disabled and glyph changes are applied in place. A button can raise a column
 * of further buttons on a bronze stem above it (the weapon choices), each rising out of it in turn.
 */
export class HudDock extends THREE.Group {
  private kit: ButtonKit;
  private buttons = new Map<string, OctagonButton>();
  private shape = '';
  private rail = new THREE.Group();
  private metal = Object.assign(bronze(41, 0x5d4730), { flatShading: true });
  private hardware = iron();
  private dark = new THREE.MeshStandardMaterial({ color: 0x0b0806, roughness: .9 });
  private bolt = boltGeometry(3.4);
  private column = new Map<string, OctagonButton>();
  private columnShape = '';
  private stem: THREE.Mesh<THREE.CylinderGeometry> | null = null;
  private rising = 0;
  /** The rail's length end to end, in CSS pixels at size 1. */
  length = 0;

  /** `kit` lets a layer's rails and menus share one set of button parts and glyph faces; without it the rail makes and disposes its own. */
  constructor(glow: THREE.Texture | null, kit?: ButtonKit) {
    super();
    this.kit = kit ?? new ButtonKit(glow); this.ownsKit = !kit;
    this.add(this.rail);
  }
  private ownsKit: boolean;

  setButtons(buttons: DockButton[]) {
    const shape = buttons.map(button => `${button.id}${button.large ? '+' : ''}${button.apart ? '|' : ''}`).join(',');
    if (shape !== this.shape) this.build(buttons, shape);
    for (const state of buttons) {
      const button = this.buttons.get(state.id)!;
      button.setIcon(state.icon); button.pressed = !!state.pressed; button.disabled = !!state.disabled;
    }
  }
  /** Raises a column of buttons above the bar button `from` (null lowers it). */
  setColumn(from: string | null, buttons: DockButton[]) {
    const shape = from ? `${from}:${buttons.map(button => button.id).join(',')}` : '';
    if (shape !== this.columnShape) {
      this.columnShape = shape; this.clearColumn();
      const base = from ? this.buttons.get(from) : null;
      if (base) {
        buttons.forEach((state, k) => {
          const button = new OctagonButton(state.id, state.icon, state.tint ?? 0xd8c08a, this.kit);
          button.userData.home = new THREE.Vector3(base.position.x, (k + 1) * STEP, 2); button.userData.index = k;
          button.position.set(base.position.x, 0, 2); this.column.set(state.id, button); this.add(button);
        });
        const height = buttons.length * STEP;
        this.stem = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, height, 10), this.metal);
        this.stem.position.set(base.position.x, height / 2, -10); this.stem.scale.y = .001; this.add(this.stem);
        this.rising = 0;
      }
    }
    for (const state of buttons) { const button = this.column.get(state.id); if (button) { button.setIcon(state.icon); button.pressed = !!state.pressed; button.disabled = !!state.disabled; } }
  }
  flash(id: string) { (this.buttons.get(id) ?? this.column.get(id))?.flash(); }

  pick(raycaster: THREE.Raycaster): string | null {
    for (const hit of raycaster.intersectObjects([...this.column.values(), ...this.buttons.values()], true)) {
      for (let object: THREE.Object3D | null = hit.object; object; object = object.parent) if (object.userData.button) return object.userData.button as string;
    }
    return null;
  }
  hover(id: string | null) { for (const [key, button] of [...this.buttons, ...this.column]) button.hovered = key === id; }

  update(dt: number, time: number) {
    for (const button of this.buttons.values()) {
      const base = button.userData.large ? LARGE : 1;
      button.update(dt, time); button.scale.multiplyScalar(base);
    }
    // The column rises one button after another, the stem growing under them; each starts a step behind the one below and the top
    // one lands as the rise ends, however tall the column.
    this.rising = Math.min(1, this.rising + dt * 5);
    const reach = 1 + Math.max(0, this.column.size - 1) * .25;
    if (this.stem) { this.stem.scale.y = Math.max(.001, ease(this.rising)); this.stem.position.y = this.stem.geometry.parameters.height * this.stem.scale.y / 2; }
    for (const button of this.column.values()) {
      const k = button.userData.index as number, home = button.userData.home as THREE.Vector3, t = ease(Math.min(1, Math.max(0, this.rising * reach - k * .25)));
      button.update(dt, time);
      button.position.set(home.x, home.y * t, home.z + button.position.z); button.scale.multiplyScalar(.4 + t * .6);
    }
  }

  private clearColumn() {
    for (const button of this.column.values()) { button.removeFromParent(); button.dispose(); }
    this.column.clear();
    if (this.stem) { this.stem.removeFromParent(); this.stem.geometry.dispose(); this.stem = null; }
  }

  private build(buttons: DockButton[], shape: string) {
    this.clearColumn(); this.columnShape = '';
    this.shape = shape;
    for (const button of this.buttons.values()) { button.removeFromParent(); button.dispose(); }
    this.buttons.clear();
    for (const child of [...this.rail.children]) { child.removeFromParent(); if (child instanceof THREE.Mesh && child.geometry !== this.bolt) child.geometry.dispose(); }
    let x = 0;
    const placed = buttons.map((state, k) => {
      if (k) x += STEP + (state.large || buttons[k - 1].large ? BUTTON * (LARGE - 1) : 0) + (state.apart ? APART : 0);
      return { state, x };
    });
    const middle = x / 2;
    for (const { state, x } of placed) {
      const button = new OctagonButton(state.id, state.icon, state.tint ?? 0xd8c08a, this.kit);
      button.userData.large = !!state.large; button.position.set(x - middle, 0, 0);
      this.buttons.set(state.id, button); this.add(button);
    }
    // The rail: a chamfered bronze bar behind the buttons, a dark channel along it, and a bolted boss at each end.
    const length = this.length = x + BUTTON * 3.4, height = BUTTON * 1.15;
    const bar = new THREE.Shape(), r = height / 2;
    bar.moveTo(-length / 2 + r, -r); bar.lineTo(length / 2 - r, -r); bar.lineTo(length / 2, 0); bar.lineTo(length / 2 - r, r);
    bar.lineTo(-length / 2 + r, r); bar.lineTo(-length / 2, 0); bar.closePath();
    const body = new THREE.Mesh(new THREE.ExtrudeGeometry(bar, { depth: 4, bevelEnabled: true, bevelThickness: 2.5, bevelSize: 2.5, bevelSegments: 1 }), this.metal);
    body.position.z = -14;
    const channel = new THREE.Mesh(new THREE.PlaneGeometry(length - height * 1.4, height * .42), this.dark);
    channel.position.z = -7.2;
    this.rail.add(body, channel);
    this.rail.add(new THREE.Mesh(copiesGeometry(this.bolt.clone(), [-1, 1].map(side => new THREE.Vector3(side * (length / 2 - r * .9), 0, -5))), this.hardware));
  }

  dispose() {
    this.clearColumn();
    for (const button of this.buttons.values()) button.dispose();
    for (const child of this.rail.children) if (child instanceof THREE.Mesh && child.geometry !== this.bolt) { child.geometry.dispose(); }
    if (this.ownsKit) this.kit.dispose();
    this.metal.dispose(); this.hardware.dispose(); this.dark.dispose(); this.bolt.dispose();
  }
}
